import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react'
import { calculateCouponPricing } from '../utils/couponCalculations'

// --- Steps du tunnel de commande ---
export type OrderStep =
  | 'SERVICE'
  | 'OPTIONS'
  | 'PICKUP'
  | 'DELIVERY'
  | 'PAYMENT'
  | 'CONFIRMATION'

export const ORDER_STEPS: OrderStep[] = [
  'SERVICE',
  'OPTIONS',
  'PICKUP',
  'DELIVERY',
  'PAYMENT',
  'CONFIRMATION',
]

// --- Types métier déjà utilisés ---
export type Service = {
  id: string
  name: string
  description?: string
  base_price: number
  image_url?: string | null
}

export type ServiceOption = {
  id: string
  service_id: string
  label: string
  code: string
  description?: string | null
  price_delta: number
  image_urls?: string[]
}

export type TransportMode = {
  id: string
  type_mode: 'PICKUP' | 'DELIVERY'
  label: string
  description?: string | null
  pricing_mode: 'FIXED' | 'PER_KM'
  fixed_price?: number | null
  price_per_km?: number | null
  min_price?: number | null
  max_distance_km?: number | null
  is_atelier?: boolean
  is_active?: boolean
}

export type DeliverySlot = {
  date: string
  start: string
  end: string
}

export type PartnerPricingResult = {
  service_base_price: number
  option_price: number
  pickup_fee: number
  delivery_fee: number
  partner_commission_pct: number
  partner_commission_amount: number
  member_discount_pct: number
  member_discount_amount: number
  original_service_price: number
  final_service_price: number
  total_price: number
  coupon_id: string | null
  partner_id: string
  partner_name: string
  partner_address: string
}

export type OrderDraft = {
  service?: Service
  option?: ServiceOption
  racketBrand?: string
  racketDescription?: string
  racketOwnerName?: string
  tensionVertical?: number
  tensionHorizontal?: number
  pickupMode?: TransportMode
  pickupPrice?: number
  pickupDistanceKm?: number
  pickupAddress?: string
  deliveryMode?: TransportMode
  deliveryAddress?: string
  deliveryDistanceKm?: number
  deliveryPrice?: number
  deliverySlot?: DeliverySlot
  totalPrice?: number
  // Partner booking fields
  isPartnerMode?: boolean
  partnerId?: string
  partnerAccountId?: string
  couponId?: string
  couponCode?: string
  partnerCommissionPct?: number
  partnerCommissionAmount?: number
  memberDiscountPct?: number
  memberDiscountAmount?: number
  originalServicePrice?: number
  partnerAddress?: string
  partnerPricing?: PartnerPricingResult
}

// --- Type du contexte ---
type OrderContextType = {
  draft: OrderDraft

  // gestion des étapes
  currentStep: OrderStep
  maxStepReached: OrderStep
  nextStep: () => void
  previousStep: () => void
  goToStep: (step: OrderStep) => void
  canGoToStep: (step: OrderStep) => boolean

  // setters métier
  setService: (service: Service) => void
  setOption: (option: ServiceOption) => void
  setRacketInfo: (data: {
    brand: string
    description?: string
    ownerName?: string
    tensionVertical: number
    tensionHorizontal: number
  }) => void
  setPickup: (mode: TransportMode,
              address?: string,
              distanceKm?: number | 0,
              price?: number
  ) => void
  setDelivery: (
    mode: TransportMode,
    address?: string | null,
    distanceKm?: number | 0,
    price?: number
  ) => void
  setDeliverySlot: (slot: DeliverySlot) => void

  computeTotal: () => number
  resetOrder: () => void

  // Partner mode
  isPartnerMode: boolean
  setPartnerMode: (config: {
    partnerId: string
    partnerAccountId: string
    partnerAddress: string
  }) => void
  setPartnerPricing: (pricing: PartnerPricingResult) => void
  clearPartnerMode: () => void
}

const OrderContext = createContext<OrderContextType | undefined>(undefined)

// --- Provider ---
export const OrderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [draft, setDraft] = useState<OrderDraft>({})
  const [currentStep, setCurrentStep] = useState<OrderStep>('SERVICE')
  const [maxStepReached, setMaxStepReached] = useState<OrderStep>('SERVICE')

  const stepIndex = (step: OrderStep): number => ORDER_STEPS.indexOf(step)

  const advanceToStep = (step: OrderStep) => {
    setCurrentStep(step)
    setMaxStepReached(prev =>
      stepIndex(step) > stepIndex(prev) ? step : prev
    )
  }

  // --- Navigation dans les étapes ---

  const nextStep = useCallback(() => {
    const idx = stepIndex(currentStep)
    const next = ORDER_STEPS[idx + 1]
    if (!next) return
    advanceToStep(next)
  }, [currentStep])

  const previousStep = useCallback(() => {
    const idx = stepIndex(currentStep)
    const prev = ORDER_STEPS[idx - 1]
    if (!prev) return
    setCurrentStep(prev)
    // on ne touche pas maxStepReached (l'utilisateur peut toujours revenir plus loin)
  }, [currentStep])

  const canGoToStep = useCallback((step: OrderStep): boolean => {
    return stepIndex(step) <= stepIndex(maxStepReached)
  }, [maxStepReached])

  const goToStep = useCallback((step: OrderStep) => {
    if (stepIndex(step) > stepIndex(maxStepReached)) return
    setCurrentStep(step)
  }, [maxStepReached])

  // --- Setters métier ---

  const setService = (service: Service) => {
    setDraft(prev => ({
      ...prev,
      service,
      option: undefined, // reset option si on change de service
    }))
    advanceToStep('OPTIONS')
  }

  const setOption = (option: ServiceOption) => {
    setDraft(prev => ({ ...prev, option }))
    advanceToStep('OPTIONS')
  }

  const setRacketInfo = ({
                           brand,
                           description,
                           ownerName,
                           tensionVertical,
                           tensionHorizontal,
                         }: {
    brand: string
    description?: string
    ownerName?: string
    tensionVertical: number
    tensionHorizontal: number
  }) => {
    setDraft(prev => ({
      ...prev,
      racketBrand: brand,
      racketDescription: description,
      racketOwnerName: ownerName,
      tensionVertical,
      tensionHorizontal,
    }))
    advanceToStep('PICKUP')
  }

  const setPickup = (
    mode: TransportMode,
    address?: string,
    distanceKm?: number,
    price?: number
    ) => {
    setDraft(prev => ({
      ...prev,
      pickupMode: mode,
      pickupAddress: address,
      pickupDistanceKm : distanceKm,
      pickupPrice: price,
    }))
    advanceToStep('DELIVERY')
  }

  const setDelivery = (
    mode: TransportMode,
    address?: string | null,
    distanceKm?: number,
    price?: number
  ) => {
    setDraft(prev => ({
      ...prev,
      deliveryMode: mode,
      deliveryAddress: address ?? undefined,
      deliveryDistanceKm: distanceKm,
      deliveryPrice: price,
    }))
    advanceToStep('PAYMENT')
  }

  const setDeliverySlot = (slot: DeliverySlot) => {
    setDraft(prev => ({ ...prev, deliverySlot: slot }))
    // on reste sur PAYMENT, c'est là que le slot est choisi en général
  }

  // --- Partner mode ---
  const setPartnerMode = useCallback((config: {
    partnerId: string
    partnerAccountId: string
    partnerAddress: string
  }) => {
    setDraft(prev => ({
      ...prev,
      isPartnerMode: true,
      partnerId: config.partnerId,
      partnerAccountId: config.partnerAccountId,
      partnerAddress: config.partnerAddress,
      pickupPrice: 0,
      deliveryPrice: 0,
      pickupAddress: config.partnerAddress,
      deliveryAddress: config.partnerAddress,
    }))
  }, [])

  const setPartnerPricing = useCallback((pricing: PartnerPricingResult) => {
    setDraft(prev => ({
      ...prev,
      partnerPricing: pricing,
      partnerCommissionPct: pricing.partner_commission_pct,
      partnerCommissionAmount: pricing.partner_commission_amount,
      memberDiscountPct: pricing.member_discount_pct,
      memberDiscountAmount: pricing.member_discount_amount,
      originalServicePrice: pricing.original_service_price,
      couponId: pricing.coupon_id ?? undefined,
    }))
  }, [])

  const clearPartnerMode = useCallback(() => {
    setDraft(prev => ({
      ...prev,
      isPartnerMode: false,
      partnerId: undefined,
      partnerAccountId: undefined,
      couponId: undefined,
      couponCode: undefined,
      partnerCommissionPct: undefined,
      partnerCommissionAmount: undefined,
      memberDiscountPct: undefined,
      memberDiscountAmount: undefined,
      originalServicePrice: undefined,
      partnerAddress: undefined,
      partnerPricing: undefined,
    }))
  }, [])

  const computeTotal = () => {
    // En mode partenaire avec pricing serveur, utiliser le total calculé par le serveur
    if (draft.isPartnerMode && draft.partnerPricing) {
      return draft.partnerPricing.total_price
    }

    let total = 0

    // --- SERVICE ---
    if (draft.service?.base_price) {
      total += draft.service.base_price
    }

    // --- OPTION ---
    if (draft.option?.price_delta) {
      total += draft.option.price_delta
    }

    // --- PICKUP ---
    if (draft.pickupMode) {
      if (draft.pickupMode.pricing_mode === "FIXED") {
        total += draft.pickupMode.fixed_price ?? 0
      } else if (draft.pickupMode.pricing_mode === "PER_KM") {
        total += draft.pickupPrice ?? 0
      }
    }

    // --- DELIVERY ---
    if (draft.deliveryMode) {
      if (draft.deliveryMode.pricing_mode === "FIXED") {
        total += draft.deliveryMode.fixed_price ?? 0
      } else if (draft.deliveryMode.pricing_mode === "PER_KM") {
        total += draft.deliveryPrice ?? 0
      }
    }

    return total
  }



  const resetOrder = useCallback(() => {
    setDraft({})
    setCurrentStep('SERVICE')
    setMaxStepReached('SERVICE')
  }, [])

  const isPartnerMode = draft.isPartnerMode === true

  const value = useMemo(
    () => ({
      draft,
      currentStep,
      maxStepReached,
      nextStep,
      previousStep,
      goToStep,
      canGoToStep,
      setService,
      setOption,
      setRacketInfo,
      setPickup,
      setDelivery,
      setDeliverySlot,
      computeTotal,
      resetOrder,
      isPartnerMode,
      setPartnerMode,
      setPartnerPricing,
      clearPartnerMode,
    }),
    [draft, currentStep, maxStepReached]
  )

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
}

// --- Hook helper ---
export const useOrder = (): OrderContextType => {
  const context = useContext(OrderContext)
  if (!context) throw new Error('useOrder must be used within an OrderProvider')
  return context
}
